import React, { createContext, useContext, useState } from 'react';

import { useAuth } from '../../../session/InternalAuthProvider';
import OrderUtils, { calculatePrice, MULTIPLE_TICKETS_DETAILS_FIELD_NAME, PriceMode } from '../../../common/OrderUtils';
import { PluginType } from '../../../common/PluginUtils';

import { DATE_FORMAT } from '../../calendarWrapper/CalendarWrapper';
import { OrderStatus, VoucherType } from '../../../api';
import { getOrderPages } from '../../../utils/AccountUtils';
import { orderPageInitialState } from '../../../editOrderUiObjects/types/OrderPageState';

export function pluginEnabled(orderCustomDetail, pluginName) {
  const item = orderCustomDetail[pluginName] === true;
  return !!item;
}

const initialCurrentState = { ...orderPageInitialState, ...OrderUtils.initialEditOrderState };

const EditOrderContext = createContext(initialCurrentState);

export const useEditOrder = () => useContext(EditOrderContext);

export const EditOrderProvider = (props) => {
  const { authState } = useAuth();
  const { account } = authState;
  const {
    order,
    cloneSourceOrder,
    voucherToRedeem,
    voucherToEdit,
    waitingListItem,
    passToRedeem,
    isCreateOrderIntent,
  } = props;

  let { orderPageId } = props;
  const experience = account.experiences[0];
  orderPageId =
    orderPageId ||
    (order ? order.order_page_id : cloneSourceOrder ? cloneSourceOrder.order_page_id : getOrderPages(account)[0].id);
  const isMonetaryVoucherRedeem = voucherToRedeem?.type === VoucherType.MONETARY;
  const shouldKeepPrice = order || (voucherToRedeem && !isMonetaryVoucherRedeem) || passToRedeem;
  const isDropInPricingFlow =
    isCreateOrderIntent &&
    !order &&
    !voucherToRedeem &&
    !voucherToEdit &&
    !passToRedeem &&
    !waitingListItem &&
    account.shared_resources?.scheduler_configs?.length > 0;
  const keptPrice = order?.price || voucherToRedeem?.price || 0;
  const initialState = {
    ...orderPageInitialState,
    ...OrderUtils.initialEditOrderState,
  };
  const orderPage = getOrderPages(account, true).find((o) => o.id === orderPageId);
  initialState.account = account;
  initialState.experience = experience;
  initialState.orderPage = orderPage;
  initialState.voucherType = VoucherType.EXPERIENCE;
  if (order) {
    initialState.details.persons = order.persons;
    initialState.details.duration = order.duration;
    initialState.details.price = order.price;
    initialState.personalDetails.firstname = order.firstname;
    initialState.personalDetails.lastname = order.lastname;
    initialState.personalDetails.alternative_full_name = order.alternative_full_name;
    initialState.personalDetails.email = order.email;
    initialState.personalDetails.phone = order.phone;
    initialState.details.custom_details = order.custom_details || OrderUtils.getDefaultCustomDetails(orderPage);
    // we want to allow override price when its manual order or payment link generated
    initialState.shouldAllowPriceOverride =
      order.status === OrderStatus.CONFIRMED || order.status === OrderStatus.PAYMENT_LINK_GENERATED;
    initialState.extraPaymentsLines = order.extra_payment_lines || [];
    initialState.calculatedPrice = calculatePrice(orderPage, initialState.details, {
      date: order.date,
      time: order.time,
    });
    initialState.order_id = order.id;
    initialState.orderOrVoucher = order;
    initialState.returningCustomerFromOrderId = order.returning_customer_from_order_id;
    initialState.details.chosen_resources = order.chosen_resources || [];
  } else if (cloneSourceOrder) {
    // A copy is a new order: it keeps the source's people and customer, but its price is calculated and it has no id.
    initialState.shouldAllowPriceOverride = true;
    initialState.details.persons = cloneSourceOrder.persons;
    initialState.details.duration = orderPage.duration;
    initialState.personalDetails = {
      firstname: cloneSourceOrder.firstname,
      lastname: cloneSourceOrder.lastname,
      alternative_full_name: cloneSourceOrder.alternative_full_name,
      email: cloneSourceOrder.email,
      phone: cloneSourceOrder.phone,
    };
    initialState.details.custom_details =
      cloneSourceOrder.custom_details || OrderUtils.getDefaultCustomDetails(orderPage);
    const calculatedPrice = calculatePrice(orderPage, initialState.details, initialState.dateAndTimeDetails);
    initialState.details.price = calculatedPrice;
    initialState.calculatedPrice = calculatedPrice;
    initialState.order_id = '';
  } else if (voucherToRedeem && !isMonetaryVoucherRedeem) {
    initialState.details.persons = voucherToRedeem.persons;
    initialState.details.duration = voucherToRedeem.duration;
    initialState.details.price = voucherToRedeem.price;
    initialState.personalDetails.firstname = voucherToRedeem.firstname;
    initialState.personalDetails.lastname = voucherToRedeem.lastname;
    initialState.personalDetails.email = voucherToRedeem.email;
    initialState.personalDetails.phone = voucherToRedeem.phone;
    initialState.details.custom_details =
      voucherToRedeem.custom_details || OrderUtils.getDefaultCustomDetails(orderPage);
    initialState.shouldAllowPriceOverride = false;
    initialState.extraPaymentsLines = [];
    initialState.orderOrVoucher = voucherToRedeem;
    initialState.details.chosen_resources = voucherToRedeem.chosen_resources || [];
  } else if (voucherToEdit) {
    initialState.voucherType = voucherToEdit.type || VoucherType.EXPERIENCE;
    initialState.details.persons = voucherToEdit.persons;
    initialState.details.duration = voucherToEdit.duration;
    initialState.details.price = voucherToEdit.price;
    initialState.personalDetails.firstname = voucherToEdit.firstname;
    initialState.personalDetails.lastname = voucherToEdit.lastname;
    initialState.personalDetails.alternative_full_name = voucherToEdit.alternative_full_name;
    initialState.personalDetails.email = voucherToEdit.email;
    initialState.personalDetails.phone = voucherToEdit.phone;
    initialState.details.custom_details = voucherToEdit.custom_details || OrderUtils.getDefaultCustomDetails(orderPage);
    initialState.shouldAllowPriceOverride = false;
    initialState.extraPaymentsLines = [];
    initialState.orderOrVoucher = voucherToEdit;
    initialState.details.chosen_resources = voucherToEdit.chosen_resources || [];
  } else if (passToRedeem) {
    initialState.personalDetails.firstname = passToRedeem.firstname;
    initialState.personalDetails.lastname = passToRedeem.lastname;
    initialState.personalDetails.email = passToRedeem.email;
    initialState.personalDetails.phone = passToRedeem.phone;
    initialState.shouldAllowPriceOverride = false;
    initialState.extraPaymentsLines = [];
    initialState.orderOrVoucher = passToRedeem;
  } else if (waitingListItem) {
    initialState.shouldAllowPriceOverride = true;
    initialState.details.persons = waitingListItem.persons;
    initialState.personalDetails.firstname = waitingListItem.firstname;
    initialState.personalDetails.lastname = waitingListItem.lastname;
    initialState.personalDetails.email = waitingListItem.email;
    initialState.personalDetails.phone = waitingListItem.phone;
    initialState.details.duration = orderPage.duration;
    initialState.details.custom_details = OrderUtils.getDefaultCustomDetails(orderPage);
    const calculatedPrice = calculatePrice(orderPage, initialState.details, initialState.dateAndTimeDetails);
    initialState.details.price = calculatedPrice;
    initialState.calculatedPrice = calculatedPrice;
    initialState.order_id = '';
  } else {
    // Orders manually added can have the price changed when adding them.
    initialState.shouldAllowPriceOverride = true;
    initialState.details.persons = orderPage.persons;
    initialState.details.duration = orderPage.duration;
    initialState.personalDetails = isMonetaryVoucherRedeem
      ? {
          firstname: voucherToRedeem.firstname,
          lastname: voucherToRedeem.lastname,
          email: voucherToRedeem.email,
          phone: voucherToRedeem.phone,
        }
      : {
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
        };
    initialState.details.custom_details = OrderUtils.getDefaultCustomDetails(orderPage);
    const calculatedPrice = calculatePrice(orderPage, initialState.details, initialState.dateAndTimeDetails);
    initialState.details.price = calculatedPrice;
    initialState.calculatedPrice = calculatedPrice;
    initialState.order_id = '';
    if (isDropInPricingFlow) {
      initialState.priceMode = PriceMode.ZERO;
    }
  }

  const [editOrderState, setEditOrderState] = useState(initialState);

  // Functional updates: the clipboard prefill calls this, setDate and setPrice back to back, and building on the
  // render-time state would let the last call drop the earlier ones.
  const setOrderDetailValue = (dataType, value) => {
    setEditOrderState((prev) => {
      const tempDetails = {
        ...prev.details,
        [dataType]: value,
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(prev.details?.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const calculatedPrice = calculatePrice(
        prev.orderPage,
        tempDetails,
        prev.dateAndTimeDetails,
        shouldUseweekendPlugin,
      );
      return {
        ...prev,
        calculatedPrice,
        details: {
          ...tempDetails,
          price: shouldKeepPrice ? keptPrice : calculatedPrice,
        },
      };
    });
  };

  const setCustomOrderDetailValue = (name, value) => {
    const tempDetails = {
      ...editOrderState.details,
      custom_details: {
        ...editOrderState.details.custom_details,
        [name]: value,
      },
    };

    const calculatedPrice = calculatePrice(editOrderState.orderPage, tempDetails, editOrderState.dateAndTimeDetails);

    setEditOrderState({
      ...editOrderState,
      calculatedPrice,
      details: {
        ...tempDetails,
        price: shouldKeepPrice ? keptPrice : calculatedPrice,
      },
    });
  };
  const calcPriceWithCustomDetails = (tempDetails, customDetails, orderPage) => {
    let shouldUseweekendPlugin = false;
    if (pluginEnabled(customDetails, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
      shouldUseweekendPlugin = true;
    }
    return calculatePrice(orderPage, tempDetails, editOrderState.dateAndTimeDetails, shouldUseweekendPlugin);
  };
  const setOrderCustomDetails = (customDetails) => {
    const tempDetails = {
      ...editOrderState.details,
      custom_details: {
        ...editOrderState.details.custom_details,
        ...customDetails,
      },
    };
    const calculatedPrice = calcPriceWithCustomDetails(tempDetails, customDetails, editOrderState.orderPage);
    setEditOrderState({
      ...editOrderState,
      calculatedPrice,
      details: {
        ...tempDetails,
        price: shouldKeepPrice ? keptPrice : calculatedPrice,
      },
    });
  };
  const setMultipleTicketsDetails = (multipleTicketsDetails) => {
    const totalPersons = Object.values(multipleTicketsDetails).reduce((sum, val) => sum + val, 0);

    const tempDetails = {
      ...editOrderState.details,
      persons: totalPersons,
      custom_details: {
        ...editOrderState.details.custom_details,
        [MULTIPLE_TICKETS_DETAILS_FIELD_NAME]: multipleTicketsDetails,
      },
    };
    const calculatedPrice = calcPriceWithCustomDetails(tempDetails, multipleTicketsDetails, editOrderState.orderPage);
    setEditOrderState({
      ...editOrderState,
      calculatedPrice,
      details: {
        ...tempDetails,
        price: shouldKeepPrice ? keptPrice : calculatedPrice,
      },
    });
  };
  const setPersonalDetails = (personalDetails) => {
    setEditOrderState({ ...editOrderState, personalDetails });
  };
  const setOrderPage = (orderPageId) => {
    const newOrderPage = getOrderPages(account, true).find((o) => o.id === orderPageId);
    // some customers (speculo) need deafult custom details otherwise it breaks
    const defaultCustomDetails = OrderUtils.getDefaultCustomDetails(newOrderPage);
    const newInitialState = {
      ...initialState,
      account: editOrderState.account,
      experience: editOrderState.experience,
      orderPage: newOrderPage,
      extraPaymentsLines: editOrderState.extraPaymentsLines,
      priceMode: editOrderState.priceMode,
      details: {
        ...initialState.details,
        persons: editOrderState.details.persons,
        duration: newOrderPage.duration,
        custom_details: defaultCustomDetails,
      },
    };
    const calculatedPrice = calcPriceWithCustomDetails(newInitialState.details, defaultCustomDetails, newOrderPage);

    setEditOrderState({
      ...newInitialState,
      order_id: editOrderState.order_id,
      shouldAllowPriceOverride: !shouldKeepPrice,
      calculatedPrice,
      details: {
        ...newInitialState.details,
        price: shouldKeepPrice ? keptPrice : calculatedPrice,
      },
    });
  };
  const setDate = (date) => {
    setEditOrderState((prev) => {
      const tempDetails = {
        ...prev.details,
      };
      let shouldUseweekendPlugin = false;
      if (pluginEnabled(prev.details?.custom_details, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS)) {
        shouldUseweekendPlugin = true;
      }
      const calculatedPrice = calculatePrice(
        prev.orderPage,
        tempDetails,
        {
          date: date.format(DATE_FORMAT),
        },
        shouldUseweekendPlugin,
      );
      return {
        ...prev,
        calculatedPrice,
        dateAndTimeDetails: {
          ...prev.dateAndTimeDetails,
          date: date.format(DATE_FORMAT),
        },
        details: {
          ...tempDetails,
          price: shouldKeepPrice ? keptPrice : calculatedPrice,
        },
      };
    });
  };
  const setPrice = (price) => {
    setEditOrderState((prev) => ({
      ...prev,
      priceMode: PriceMode.OVERRIDE,
      details: {
        ...prev.details,
        price,
      },
    }));
  };

  const setVoucherType = (voucherType) => {
    setEditOrderState((prev) => ({
      ...prev,
      voucherType,
      details: { ...prev.details, price: voucherType === VoucherType.MONETARY ? 0 : prev.calculatedPrice },
    }));
  };

  const setCreatePaymentLink = (shouldCreatePaymentLink) => {
    if (!isDropInPricingFlow) {
      return;
    }
    setEditOrderState((prev) =>
      prev.priceMode === PriceMode.OVERRIDE
        ? prev
        : { ...prev, priceMode: shouldCreatePaymentLink ? PriceMode.CALCULATED : PriceMode.ZERO },
    );
  };

  // Extra payments are written server-side the moment the dialog is submitted, so the
  // drawer has to catch up on a freshly fetched order. Merge surgically -- the drawer may
  // be holding unsaved edits in details/personalDetails, and reseeding would discard them.
  // Functional setter: this runs after an await, so a captured editOrderState would be stale.
  const applyRefreshedOrder = (freshOrder) => {
    setEditOrderState((prev) => ({
      ...prev,
      extraPaymentsLines: freshOrder.extra_payment_lines || [],
      orderOrVoucher: { ...prev.orderOrVoucher, ...freshOrder },
      paymentLinksRefreshKey: (prev.paymentLinksRefreshKey || 0) + 1,
    }));
  };
  const { children } = props;
  const exposedEditOrderState =
    editOrderState.priceMode === PriceMode.ZERO
      ? { ...editOrderState, details: { ...editOrderState.details, price: 0 } }
      : editOrderState;
  return (
    <EditOrderContext.Provider
      value={{
        editOrderState: exposedEditOrderState,
        setOrderDetailValue,
        setCustomOrderDetailValue,
        setOrderCustomDetails,
        setEditOrderState,
        setPersonalDetails,
        setOrderPage,
        setDate,
        setPrice,
        setVoucherType,
        setCreatePaymentLink,
        isDropInPricingFlow,
        applyRefreshedOrder,
        setMultipleTicketsDetails,
      }}>
      {children}
    </EditOrderContext.Provider>
  );
};
